import ImageIcon from "lucide-react/dist/esm/icons/image";
import Link2 from "lucide-react/dist/esm/icons/link-2";
import Pencil from "lucide-react/dist/esm/icons/pencil";
import { cn, elementId, imageSource, sanitizeUrl, textStyleFor } from "../lib";
import { useEditor } from "../store";
import type { Item, Section, TextStyle } from "../types";

const cssFromTextStyle = (style: TextStyle): React.CSSProperties => ({
  fontSize: style.fontSize || undefined,
  fontWeight: style.fontWeight || undefined,
  color: style.color || undefined,
  textAlign: style.align,
  lineHeight: style.lineHeight,
  letterSpacing:
    style.letterSpacing == null ? undefined : `${style.letterSpacing}px`,
  maxWidth: style.maxWidth || undefined,
  translate:
    style.offsetX || style.offsetY
      ? `${style.offsetX ?? 0}px ${style.offsetY ?? 0}px`
      : undefined,
});

export function EditableText({
  section,
  field,
  item,
  as: Tag = "div",
  className = "",
}: {
  section: Section;
  field: string;
  item?: Item;
  as?: keyof React.JSX.IntrinsicElements;
  className?: string;
}) {
  const { editing, preview, selection, select, updateSection, updateItem } =
    useEditor();
  const enabled = editing && !preview;
  const value = String(
    item ? (item[field as keyof Item] ?? "") : (section.content[field] ?? ""),
  );
  const id = elementId(section.id, field, item?.id);
  const selected = selection?.elementId === id;
  const commit = (text: string) =>
    item
      ? updateItem(section.id, item.id, (target) => {
          (target as unknown as Record<string, string>)[field] = text;
        })
      : updateSection(section.id, (target) => {
          target.content[field] = text;
        });
  return (
    <Tag
      className={cn(
        className,
        enabled && "cms-editable cms-editable-text",
        selected && "cms-selected",
      )}
      contentEditable={enabled}
      suppressContentEditableWarning
      style={cssFromTextStyle(textStyleFor(section, field, item))}
      data-cms-id={id}
      data-cms-kind="text"
      data-cms-section={section.id}
      data-cms-item={item?.id}
      data-cms-field={field}
      data-placeholder="Type something…"
      onClick={(event) => {
        if (enabled) {
          event.preventDefault();
          event.stopPropagation();
          select({
            sectionId: section.id,
            kind: "text",
            field,
            itemId: item?.id,
            elementId: id,
          });
        }
      }}
      onBlur={(event) => {
        if (enabled)
          commit(event.currentTarget.textContent?.slice(0, 2000) ?? "");
      }}
      onKeyDown={(event) => {
        if (
          event.key === "Enter" &&
          !event.shiftKey &&
          ["A", "BUTTON", "SPAN", "B", "STRONG", "SMALL"].includes(
            event.currentTarget.tagName,
          )
        ) {
          event.preventDefault();
          event.currentTarget.blur();
        }
      }}
    >
      {value}
    </Tag>
  );
}

export function GlobalEditableText({
  field,
  as: Tag = "span",
  className = "",
}: {
  field: string;
  as?: keyof React.JSX.IntrinsicElements;
  className?: string;
}) {
  const { site, editing, preview, selection, select, updateSite } = useEditor();
  const enabled = editing && !preview;
  const id = `global:${field}`;
  const value = String(
    site.globalContent[field as keyof typeof site.globalContent] ?? "",
  );
  return (
    <Tag
      className={cn(
        className,
        enabled && "cms-editable cms-editable-text",
        selection?.elementId === id && "cms-selected",
      )}
      contentEditable={enabled}
      suppressContentEditableWarning
      style={cssFromTextStyle(site.globalTextStyles?.[field] ?? {})}
      data-cms-id={id}
      data-cms-kind="global"
      data-cms-field={field}
      onClick={(event) => {
        if (!enabled) return;
        event.preventDefault();
        event.stopPropagation();
        select({ kind: "global", field, elementId: id });
      }}
      onBlur={(event) => {
        if (!enabled) return;
        updateSite((target) => {
          (target.globalContent as unknown as Record<string, string>)[field] =
            event.currentTarget.textContent?.slice(0, 1000) ?? "";
        });
      }}
    >
      {value}
    </Tag>
  );
}

export function EditableImage({
  section,
  item,
  field = "image",
  altField = "alt",
  className = "",
  loading = "lazy",
}: {
  section: Section;
  item?: Item;
  field?: string;
  altField?: string;
  className?: string;
  loading?: "eager" | "lazy";
}) {
  const { site, editing, preview, selection, select } = useEditor();
  const enabled = editing && !preview;
  const fallback = String(item ? item.image : section.content[field]);
  const assetId = String(item?.assetId ?? section.content.assetId ?? "");
  const asset = site.assets.find((entry) => entry.id === assetId);
  const src = imageSource(site, fallback, assetId);
  const alt = String(
    (item ? item.alt : section.content[altField]) || asset?.alt || "",
  );
  const id = elementId(section.id, field, item?.id);
  const selected = selection?.elementId === id;
  const focalX = Number(item?.focalX ?? section.content.focalX ?? 50);
  const focalY = Number(item?.focalY ?? section.content.focalY ?? 50);
  const radius = Number(item?.radius ?? section.content.imageRadius ?? 0);
  const overlay = Number(item?.overlay ?? section.content.imageOverlay ?? 0);
  const zoom = Number(item?.zoom ?? section.content.imageZoom ?? 1);
  const aspectRatio = String(
    item?.aspectRatio ?? section.content.imageAspectRatio ?? "auto",
  );
  const brightness = Number(
    item?.brightness ?? section.content.imageBrightness ?? 100,
  );
  return (
    <div
      className={cn(
        "editable-image",
        className,
        enabled && "cms-editable cms-editable-image",
        selected && "cms-selected",
      )}
      style={
        {
          borderRadius: radius || undefined,
          aspectRatio: aspectRatio === "auto" ? undefined : aspectRatio,
          "--image-overlay": overlay / 100,
        } as React.CSSProperties
      }
      data-cms-id={id}
      data-cms-kind="image"
      data-cms-section={section.id}
      data-cms-item={item?.id}
      data-cms-field={field}
      onClick={(event) => {
        if (enabled) {
          event.preventDefault();
          event.stopPropagation();
          select({
            sectionId: section.id,
            kind: "image",
            field,
            itemId: item?.id,
            elementId: id,
          });
        }
      }}
    >
      <img
        src={src}
        alt={alt}
        loading={loading}
        style={{
          objectPosition: `${focalX}% ${focalY}%`,
          scale: zoom,
          filter: `brightness(${brightness}%)`,
        }}
      />
      {overlay > 0 && (
        <i className="editable-image-overlay" aria-hidden="true" />
      )}
      {enabled && (
        <span className="image-edit-badge">
          <ImageIcon size={13} /> Replace
        </span>
      )}
    </div>
  );
}

export function EditableIcon({
  section,
  item,
  field = "icon",
  children,
  className = "",
}: {
  section: Section;
  item?: Item;
  field?: string;
  children: React.ReactNode;
  className?: string;
}) {
  const { editing, preview, selection, select } = useEditor();
  const enabled = editing && !preview;
  const id = elementId(section.id, field, item?.id);
  return (
    <span
      className={cn(
        className,
        enabled && "cms-editable cms-editable-icon",
        selection?.elementId === id && "cms-selected",
      )}
      data-cms-id={id}
      data-cms-kind="icon"
      data-cms-section={section.id}
      data-cms-item={item?.id}
      data-cms-field={field}
      onClick={(event) => {
        if (!enabled) return;
        event.preventDefault();
        event.stopPropagation();
        select({
          sectionId: section.id,
          kind: "icon",
          field,
          itemId: item?.id,
          elementId: id,
        });
      }}
    >
      {children}
    </span>
  );
}

export function EditableLink({
  section,
  field,
  linkField = `${field}Link`,
  item,
  children,
  className = "",
}: {
  section: Section;
  field: string;
  linkField?: string;
  item?: Item;
  children: React.ReactNode;
  className?: string;
}) {
  const { editing, preview, select } = useEditor();
  const enabled = editing && !preview;
  const href = String(
    item
      ? item[linkField as keyof Item] ?? item.link ?? "#contact"
      : section.content[linkField] ?? "#contact",
  );
  const id = elementId(section.id, linkField, item?.id);
  return (
    <a
      href={enabled ? undefined : sanitizeUrl(href)}
      className={cn(className, enabled && "cms-editable cms-editable-link")}
      data-cms-id={id}
      data-cms-kind="link"
      data-cms-section={section.id}
      data-cms-item={item?.id}
      data-cms-field={field}
      data-cms-link-field={linkField}
      onClick={(event) => {
        if (enabled) {
          event.preventDefault();
          event.stopPropagation();
          select({
            sectionId: section.id,
            kind: "link",
            field,
            linkField,
            itemId: item?.id,
            elementId: id,
          });
        }
      }}
    >
      {children}
      {enabled && <Pencil size={12} />}
    </a>
  );
}

export function InlineHint({ type }: { type: "text" | "link" }) {
  return (
    <span className="inline-hint">
      {type === "link" ? <Link2 size={12} /> : <Pencil size={12} />} Click to
      edit
    </span>
  );
}
